import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Database,
  Trash2,
  RefreshCw,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileJson,
  Upload,
  Info,
  Building2,
  Globe,
  CalendarDays,
  Sparkles,
  Layers,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "../../../utils/cn";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import Input from "../../../components/ui/Input";
import { PageLayout } from "../../../components/ui/PageLayout";
import { getCompanies } from "../../../api/General/Company";
import {
  seedCompany as seedCompanyApi,
  getSeedStatus,
  clearCompany as clearCompanyApi,
  getSeedModules,
  SeedJobStatus,
  SeedModuleDescriptor,
} from "../../../api/Development/seeding";

interface CompanyDto {
  id: number;
  name: string;
}

// ---------- CompanyProfile types & validation ----------

interface LocaleShape {
  primaryLanguage: string;
  currency: string;
  timezone: string;
  country: string;
}

interface WorkingWeekShape {
  off: string[];
}

interface BranchShape {
  name: string;
  city: string;
  country: string;
  isHq: boolean;
}

interface DepartmentShape {
  name: string;
  headcountRatio: number;
  titles: string[];
}

interface SalaryBandShape {
  min: number;
  max: number;
}

interface ShiftPatternShape {
  name: string;
  start: string;
  end: string;
  weekends: boolean;
}

interface AssetCategoryShape {
  name: string;
  samples: string[];
  minPrice: number;
  maxPrice: number;
}

interface GoalCategoryShape {
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface OnboardingTaskShape {
  name: string;
  department: string;
  durationDays: number;
}

interface CompanyProfile {
  companyName: string;
  companyNarrative: string;
  sourceCitations: string[];
  locale: LocaleShape;
  workingWeek: WorkingWeekShape;
  branches: BranchShape[];
  departments: DepartmentShape[];
  roleLevelDistribution: Record<string, number>;
  salaryBands: Record<string, SalaryBandShape>;
  commonSkills: string[];
  shiftPatterns: ShiftPatternShape[];
  assetCategories: AssetCategoryShape[];
  goalCategories: GoalCategoryShape[];
  onboardingTasks: OnboardingTaskShape[];
  claimTypes: string[];
  nationalityMix: Record<string, number>;
  featureFlagsToEnable: string[];
}

const REQUIRED_ROLE_LEVELS = [
  "Executive",
  "SeniorManager",
  "Manager",
  "Senior",
  "MidLevel",
  "Junior",
] as const;

const ALLOWED_FEATURE_FLAGS = [
  "people",
  "attendance",
  "leaves",
  "payroll",
  "performance",
  "engagement",
  "assets",
  "onboarding",
  "tournaments",
  "claims",
];

const TIME_RE = /^\d{2}:\d{2}$/;

interface ValidationResult {
  ok: boolean;
  error?: string;
  profile?: CompanyProfile;
}

function lowercaseFirst(key: string): string {
  return key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
}

// Fields typed as Dictionary<string, X> on the server. Their keys are domain
// values (RoleLevel names, ISO country codes) that must be preserved exactly —
// not DTO property names that we can safely lowercase.
const DICTIONARY_FIELDS = new Set([
  "roleLevelDistribution",
  "salaryBands",
  "nationalityMix",
]);

// Normalize DTO property names to camelCase so a profile emitted with either
// casing is accepted, mirroring the backend's PropertyNameCaseInsensitive
// deserialization. Skips recursion into dictionary-valued fields so their
// keys (which carry meaning — "Executive", "SA") are left untouched.
function normalizeKeys(input: unknown, parentKey?: string): unknown {
  if (Array.isArray(input)) return input.map((v) => normalizeKeys(v));
  if (input && typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const normKey = lowercaseFirst(k);
      if (DICTIONARY_FIELDS.has(normKey) && v && typeof v === "object" && !Array.isArray(v)) {
        // Preserve inner dict keys verbatim; still normalize *their* values
        // (e.g. salaryBands["Executive"] is {min, max} which may need normalizing).
        const inner: Record<string, unknown> = {};
        for (const [dk, dv] of Object.entries(v as Record<string, unknown>)) {
          inner[dk] = normalizeKeys(dv);
        }
        out[normKey] = inner;
      } else {
        out[normKey] = normalizeKeys(v, normKey);
      }
    }
    return out;
  }
  return input;
}

function validateProfile(text: string): ValidationResult {
  const trimmed = (text ?? "").trim();
  if (!trimmed) {
    return { ok: false, error: "" };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: `Invalid JSON: ${msg}` };
  }

  const p = normalizeKeys(parsed) as Partial<CompanyProfile>;
  const errors: string[] = [];
  const MAX = 5;
  const push = (m: string) => {
    if (errors.length < MAX) errors.push(m);
  };

  if (typeof p.companyName !== "string" || !p.companyName.trim()) {
    push("companyName is required (non-empty string).");
  }
  if (typeof p.companyNarrative !== "string" || !p.companyNarrative.trim()) {
    push("companyNarrative is required (non-empty string).");
  }

  if (!Array.isArray(p.branches) || p.branches.length === 0) {
    push("branches must have at least one entry.");
  } else if (!p.branches.some((b) => b?.isHq === true)) {
    push("At least one branch must have isHq = true.");
  }

  if (!Array.isArray(p.departments) || p.departments.length === 0) {
    push("departments must have at least one entry.");
  } else {
    const sum = p.departments.reduce(
      (acc, d) => acc + (typeof d?.headcountRatio === "number" ? d.headcountRatio : 0),
      0,
    );
    if (Math.abs(sum - 1.0) > 0.02) {
      push(`departments.headcountRatio must sum to 1.0 (±0.02). Actual: ${sum.toFixed(3)}.`);
    }
  }

  const rld = p.roleLevelDistribution;
  if (!rld || typeof rld !== "object") {
    push("roleLevelDistribution is required.");
  } else {
    const missing = REQUIRED_ROLE_LEVELS.filter((k) => !(k in rld));
    if (missing.length > 0) {
      push(`roleLevelDistribution is missing keys: ${missing.join(", ")}.`);
    }
    const sum = Object.values(rld).reduce(
      (acc: number, v) => acc + (typeof v === "number" ? v : 0),
      0,
    );
    if (Math.abs(sum - 1.0) > 0.02) {
      push(`roleLevelDistribution must sum to 1.0 (±0.02). Actual: ${sum.toFixed(3)}.`);
    }
  }

  const bands = p.salaryBands;
  if (!bands || typeof bands !== "object") {
    push("salaryBands is required.");
  } else {
    const missing = REQUIRED_ROLE_LEVELS.filter((k) => !(k in bands));
    if (missing.length > 0) {
      push(`salaryBands is missing keys: ${missing.join(", ")}.`);
    }
    for (const key of REQUIRED_ROLE_LEVELS) {
      const band = bands[key] as SalaryBandShape | undefined;
      if (band && typeof band.min === "number" && typeof band.max === "number") {
        if (band.min >= band.max) {
          push(`salaryBands.${key}.min must be < max.`);
        }
      }
    }
  }

  if (!Array.isArray(p.shiftPatterns) || p.shiftPatterns.length === 0) {
    push("shiftPatterns must have at least one entry.");
  } else {
    for (let i = 0; i < p.shiftPatterns.length; i++) {
      const s = p.shiftPatterns[i];
      if (!s || typeof s.start !== "string" || !TIME_RE.test(s.start)) {
        push(`shiftPatterns[${i}].start must match HH:mm.`);
        break;
      }
      if (!s || typeof s.end !== "string" || !TIME_RE.test(s.end)) {
        push(`shiftPatterns[${i}].end must match HH:mm.`);
        break;
      }
    }
  }

  if (Array.isArray(p.featureFlagsToEnable) && p.featureFlagsToEnable.length > 0) {
    for (const flag of p.featureFlagsToEnable) {
      if (typeof flag !== "string" || !ALLOWED_FEATURE_FLAGS.includes(flag)) {
        push(
          `featureFlagsToEnable contains invalid value '${String(flag)}'. Allowed: ${ALLOWED_FEATURE_FLAGS.join(", ")}.`,
        );
        break;
      }
    }
  }

  if (p.nationalityMix && typeof p.nationalityMix === "object") {
    const values = Object.values(p.nationalityMix);
    if (values.length > 0) {
      const sum = values.reduce(
        (acc: number, v) => acc + (typeof v === "number" ? v : 0),
        0,
      );
      if (Math.abs(sum - 1.0) > 0.02) {
        push(`nationalityMix values must sum to 1.0 (±0.02). Actual: ${sum.toFixed(3)}.`);
      }
    }
  }

  if (!Array.isArray(p.sourceCitations) || p.sourceCitations.length === 0) {
    push("sourceCitations must have at least one entry.");
  }

  if (errors.length > 0) {
    return { ok: false, error: errors.join("\n") };
  }

  return { ok: true, profile: p as CompanyProfile };
}

// ---------- Component ----------

type InputMode = "paste" | "upload";

const Seeding: React.FC = () => {
  const { t } = useTranslation();
  // Company list
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Form
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [employeeCount, setEmployeeCount] = useState<number>(50);
  const [months, setMonths] = useState<number>(24);

  // Profile input
  const [inputMode, setInputMode] = useState<InputMode>("paste");
  const [profileText, setProfileText] = useState<string>("");
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [dragActive, setDragActive] = useState(false);

  // Per-module toggles. Modules are loaded once on mount; the set of *disabled*
  // module names is what we track (default = all on). When the set is empty the
  // seed request omits enabledModules so the backend falls back to "run all".
  const [modules, setModules] = useState<SeedModuleDescriptor[]>([]);
  const [disabledModules, setDisabledModules] = useState<Set<string>>(new Set());
  const [modulesExpanded, setModulesExpanded] = useState<boolean>(false);

  // Job tracking
  const [jobStatus, setJobStatus] = useState<SeedJobStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // UI
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    setError(null);
    try {
      const data = await getCompanies();
      const payload = (data as Record<string, unknown>)?.payload ?? data;
      const items: CompanyDto[] = (
        (payload as Array<Record<string, unknown>>) || []
      ).map((c) => ({
        id: c.id as number,
        name: c.name as string,
      }));
      setCompanies(items);
    } catch (e: unknown) {
      const err = e as Record<string, Record<string, string>>;
      setError(err?.error?.description || err?.message?.toString() || "Unable to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Load the module catalog once. A failure here isn't fatal — the page still
  // works with all-on semantics; we just won't render per-module toggles.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSeedModules();
        if (!cancelled) setModules(data);
      } catch {
        /* silent — toggles are optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const startPolling = useCallback((jobId: string) => {
    setPolling(true);
    if (pollingRef.current) clearInterval(pollingRef.current);

    const poll = async () => {
      try {
        const status = await getSeedStatus(jobId);
        setJobStatus(status);
        if (status.status === "Completed" || status.status === "Failed") {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPolling(false);
        }
      } catch {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPolling(false);
      }
    };

    poll(); // immediate first check
    pollingRef.current = setInterval(poll, 2000);
  }, []);

  // Validate profile (memoized on text)
  const validation = useMemo<ValidationResult>(
    () => validateProfile(profileText),
    [profileText],
  );

  const isBusy = polling;
  const isCompleted = jobStatus?.status === "Completed";
  const isFailed = jobStatus?.status === "Failed";
  const progressPct = jobStatus ? Math.round(jobStatus.progress) : 0;

  const companyOptions = companies.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  const selectedCompanyName =
    companies.find((c) => String(c.id) === selectedCompanyId)?.name ?? "";

  const handleSeed = async () => {
    if (!selectedCompanyId) return;
    if (!validation.ok) return;
    setError(null);
    setJobStatus(null);
    setConfirmClear(false);
    try {
      // Compute the whitelist *only* when the user actually turned something
      // off. Sending an empty array would mean "run nothing"; sending undefined
      // preserves the server's all-on default.
      const enabledModules = modules.length > 0 && disabledModules.size > 0
        ? modules
            .map((m) => m.name)
            .filter((n) => !disabledModules.has(n))
        : undefined;

      const resp = await seedCompanyApi(Number(selectedCompanyId), {
        months,
        employeeCount,
        profileJson: profileText,
        enabledModules,
      });
      setJobStatus({
        jobId: resp.jobId,
        status: resp.status,
        progress: 0,
        currentModule: "",
        message: "Job queued...",
        summary: {},
        durationMs: 0,
      });
      startPolling(resp.jobId);
    } catch (e: unknown) {
      const err = e as Record<string, Record<string, string>>;
      setError(err?.error?.description || err?.message?.toString() || "Failed to start seeding");
    }
  };

  const handleClear = async () => {
    if (!selectedCompanyId) return;
    setError(null);
    setJobStatus(null);
    setConfirmClear(false);
    try {
      const resp = await clearCompanyApi(Number(selectedCompanyId));
      setJobStatus({
        jobId: resp.jobId,
        status: resp.status,
        progress: 0,
        currentModule: "",
        message: "Cleanup job queued...",
        summary: {},
        durationMs: 0,
      });
      startPolling(resp.jobId);
    } catch (e: unknown) {
      const err = e as Record<string, Record<string, string>>;
      setError(err?.error?.description || err?.message?.toString() || "Failed to start cleanup");
    }
  };

  // File upload handling
  const readFileAsText = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        setProfileText(result);
        setUploadedFileName(file.name);
      }
    };
    reader.onerror = () => {
      setError("Failed to read file.");
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) readFileAsText(file);
    // Allow picking the same file again later
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readFileAsText(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const profile = validation.ok ? validation.profile! : null;
  const canSeed = !!selectedCompanyId && validation.ok && !isBusy;

  return (
    <PageLayout
      title={t('Data Seeding')}
      description={t('Generate realistic demo data for a company from a Claude-authored Company Profile. Useful for making dashboards and reports look alive in demos and testing.')}
      icon={Database}
      accentColor="slate"
      maxWidth="5xl"
    >
      <div className="space-y-6">
        {/* Helper <details> — discoverable instructions */}
        <details className="group">
          <summary className="cursor-pointer text-sm text-primary hover:underline inline-flex items-center gap-1">
            <Info className="w-3.5 h-3.5" />
            How to generate a profile
          </summary>
          <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground leading-relaxed">
            Open{" "}
            <code className="px-1 py-0.5 bg-white border border-border rounded font-mono text-[11px]">
              docs/seeding/company-profile-prompt.md
            </code>{" "}
            in the repo. Paste it into Claude with:
            <pre className="mt-2 p-2 bg-white border border-border rounded font-mono text-[11px] whitespace-pre-wrap">
{`Company: <name>, URL: <url>, Size: <N> employees, <M> months history.`}
            </pre>
            Paste the response JSON above.
          </div>
        </details>

      {/* Form */}
      <div className="border border-border rounded-lg p-5 space-y-5 bg-white">
        {/* Company selector */}
        <Select
          label="Company"
          placeholder="Select a company"
          options={companyOptions}
          value={selectedCompanyId}
          onChange={(val: string) => setSelectedCompanyId(val)}
          searchable
          loading={loadingCompanies}
          required
        />

        {/* Company Profile panel */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Company Profile</label>
            {uploadedFileName && inputMode === "upload" && (
              <span className="text-xs text-muted-foreground truncate max-w-[240px]">
                {uploadedFileName}
              </span>
            )}
          </div>

          {/* Segmented toggle */}
          <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5 text-sm">
            <button
              type="button"
              onClick={() => setInputMode("paste")}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] transition-colors",
                inputMode === "paste"
                  ? "bg-white shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <FileJson className="w-3.5 h-3.5" />
              Paste JSON
            </button>
            <button
              type="button"
              onClick={() => setInputMode("upload")}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] transition-colors",
                inputMode === "upload"
                  ? "bg-white shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Upload className="w-3.5 h-3.5" />
              Upload file
            </button>
          </div>

          {/* Paste mode */}
          {inputMode === "paste" && (
            <textarea
              value={profileText}
              onChange={(e) => setProfileText(e.target.value)}
              rows={12}
              placeholder="Paste the JSON produced by Claude using docs/seeding/company-profile-prompt.md"
              spellCheck={false}
              className={cn(
                "w-full min-h-[240px] rounded-md border border-border bg-white",
                "px-3 py-2 text-xs font-mono leading-relaxed",
                "placeholder:text-muted-foreground",
                "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
                "resize-y",
              )}
            />
          )}

          {/* Upload mode */}
          {inputMode === "upload" && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={cn(
                "rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors",
                dragActive
                  ? "border-primary bg-primary/5"
                  : "border-border bg-muted/20 hover:bg-muted/30",
              )}
            >
              <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-foreground">
                Drag a <code className="font-mono text-xs">.json</code> file here, or
              </p>
              <label className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-md border border-border bg-white text-sm cursor-pointer hover:bg-muted/30">
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={handleFileChange}
                />
                Choose file
              </label>
              {profileText && uploadedFileName && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Loaded: <span className="font-mono">{uploadedFileName}</span> · {profileText.length.toLocaleString()} chars
                </p>
              )}
            </div>
          )}

          {/* Validation indicator */}
          <ValidationIndicator text={profileText} validation={validation} />
        </div>

        {/* Preview card */}
        {profile && <ProfilePreview profile={profile} />}

        {/* Per-module toggles */}
        {modules.length > 0 && (
          <ModuleToggles
            modules={modules}
            disabled={disabledModules}
            onChange={setDisabledModules}
            expanded={modulesExpanded}
            onToggleExpanded={() => setModulesExpanded((prev) => !prev)}
          />
        )}

        {/* Employee count + months */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Employee Count"
              type="number"
              value={employeeCount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmployeeCount(Math.max(1, Number(e.target.value)))
              }
              min={1}
              max={500}
            />
            <Input
              label="Months of History"
              type="number"
              value={months}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMonths(Math.max(1, Number(e.target.value)))
              }
              min={1}
              max={60}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            These override any sizing hints in the profile.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="default"
            disabled={!canSeed}
            loading={isBusy && !isFailed && !isCompleted}
            onClick={handleSeed}
          >
            <Play className="w-4 h-4 mr-2" />
            Seed Company
          </Button>

          {!confirmClear ? (
            <Button
              variant="destructive"
              disabled={isBusy || !selectedCompanyId}
              onClick={() => setConfirmClear(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Seeded Data
            </Button>
          ) : (
            <div className="flex items-center gap-2 border border-destructive/30 rounded-md px-3 py-1.5 bg-red-50">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <span className="text-sm text-destructive">
                Clear all seeded data for {selectedCompanyName}?
              </span>
              <Button variant="destructive" size="sm" onClick={handleClear}>
                Yes, clear
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmClear(false)}
              >
                Cancel
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={loadCompanies}
            disabled={loadingCompanies}
            className="ml-auto"
          >
            <RefreshCw
              className={cn(
                "w-4 h-4 mr-1",
                loadingCompanies && "animate-spin"
              )}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-destructive/30 bg-red-50 text-destructive">
          <XCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Job Progress */}
      {jobStatus && !isFailed && !isCompleted && (
        <div className="border border-border rounded-lg p-5 space-y-3 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm font-medium">
                {jobStatus.status === "Queued" ? "Queued" : "Running"}
              </span>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              {jobStatus.jobId.substring(0, 8)}...
            </span>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{jobStatus.currentModule || "Initializing..."}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {jobStatus.message && (
            <p className="text-xs text-muted-foreground">{jobStatus.message}</p>
          )}
        </div>
      )}

      {/* Completed */}
      {jobStatus && isCompleted && (
        <div className="border border-green-200 rounded-lg p-5 space-y-3 bg-green-50">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              {jobStatus.message || "Seeding completed"}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {(jobStatus.durationMs / 1000).toFixed(1)}s
            </span>
          </div>

          {/* Summary grid */}
          {Object.keys(jobStatus.summary).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-1">
              {Object.entries(jobStatus.summary).map(([key, value]) => (
                <div
                  key={key}
                  className="rounded-md border border-green-200 bg-white px-3 py-2"
                >
                  <p className="text-xs text-muted-foreground">{key}</p>
                  <p className="text-lg font-semibold leading-tight">
                    {typeof value === "number" ? value.toLocaleString() : value}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Failed */}
      {jobStatus && isFailed && (
        <div className="border border-destructive/30 rounded-lg p-5 space-y-2 bg-red-50">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-destructive" />
            <span className="text-sm font-medium text-destructive">
              Job failed
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {(jobStatus.durationMs / 1000).toFixed(1)}s
            </span>
          </div>
          {jobStatus.error && (
            <pre className="text-xs text-destructive bg-red-100 rounded p-2 overflow-x-auto whitespace-pre-wrap">
              {jobStatus.error}
            </pre>
          )}
          {jobStatus.message && (
            <p className="text-sm text-muted-foreground">{jobStatus.message}</p>
          )}
        </div>
      )}
      </div>
    </PageLayout>
  );
};

// ---------- Module toggles ----------

interface ModuleTogglesProps {
  modules: SeedModuleDescriptor[];
  disabled: Set<string>;
  onChange: (next: Set<string>) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
}

const ModuleToggles: React.FC<ModuleTogglesProps> = ({
  modules,
  disabled,
  onChange,
  expanded,
  onToggleExpanded,
}) => {
  const enabledCount = modules.length - disabled.size;
  const allEnabled = disabled.size === 0;

  const toggleOne = (name: string) => {
    const next = new Set(disabled);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(next);
  };

  const enableAll = () => onChange(new Set());
  const disableAll = () => onChange(new Set(modules.map((m) => m.name)));

  return (
    <div className="rounded-lg border border-border bg-muted/10">
      <button
        type="button"
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/20 transition-colors rounded-t-lg"
      >
        <span className="inline-flex items-center gap-2 font-medium">
          <Layers className="w-4 h-4 text-primary" />
          Modules
          <span className="text-xs font-normal text-muted-foreground">
            {allEnabled
              ? `All ${modules.length} enabled`
              : `${enabledCount} of ${modules.length} enabled`}
          </span>
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={enableAll}
              className="text-primary hover:underline"
            >
              Enable all
            </button>
            <span className="text-muted-foreground">·</span>
            <button
              type="button"
              onClick={disableAll}
              className="text-primary hover:underline"
            >
              Disable all
            </button>
            <span className="ml-auto text-muted-foreground">
              Order matches run order. Dependencies (e.g. Payroll needs Employees) aren&apos;t enforced — toggle with care.
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {modules.map((m) => {
              const isOn = !disabled.has(m.name);
              return (
                <label
                  key={m.name}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs cursor-pointer transition-colors",
                    isOn
                      ? "border-primary/30 bg-primary/5 text-foreground"
                      : "border-border bg-white text-muted-foreground line-through",
                  )}
                >
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 accent-primary"
                    checked={isOn}
                    onChange={() => toggleOne(m.name)}
                  />
                  <span className="truncate font-mono tabular-nums text-[10px] text-muted-foreground">
                    {String(m.order).padStart(2, "0")}
                  </span>
                  <span className="truncate">{m.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Validation indicator ----------

const ValidationIndicator: React.FC<{ text: string; validation: ValidationResult }> = ({
  text,
  validation,
}) => {
  const trimmed = text.trim();

  if (!trimmed) {
    return (
      <p className="text-xs text-muted-foreground">
        No profile yet — paste or upload the JSON Claude produced.
      </p>
    );
  }

  if (validation.ok && validation.profile) {
    const { profile } = validation;
    return (
      <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
        <span>
          Valid profile for <span className="font-medium">&ldquo;{profile.companyName}&rdquo;</span> —{" "}
          {profile.branches.length} {profile.branches.length === 1 ? "branch" : "branches"},{" "}
          {profile.departments.length}{" "}
          {profile.departments.length === 1 ? "department" : "departments"}.
        </span>
      </div>
    );
  }

  // Error case
  const errLines = (validation.error ?? "").split("\n").filter(Boolean);
  const isParseError = errLines.length === 1 && errLines[0].startsWith("Invalid JSON:");

  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-red-50 px-3 py-2 text-xs text-destructive">
      <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
      {isParseError ? (
        <span className="break-words">{errLines[0]}</span>
      ) : (
        <ul className="list-disc pl-4 space-y-0.5">
          {errLines.map((line, i) => (
            <li key={i} className="break-words">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------- Preview card ----------

const Chip: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <span
    className={cn(
      "px-2 py-0.5 rounded-full text-xs border",
      className ?? "border-border bg-muted/40 text-muted-foreground",
    )}
  >
    {children}
  </span>
);

const ProfilePreview: React.FC<{ profile: CompanyProfile }> = ({ profile }) => {
  const localeText = [
    profile.locale?.country,
    profile.locale?.currency,
    profile.locale?.timezone,
  ]
    .filter(Boolean)
    .join(" · ");

  const weekendOff = profile.workingWeek?.off?.length
    ? profile.workingWeek.off.join(", ")
    : "—";

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4 space-y-4">
      {/* Top row */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            {profile.companyName}
          </h3>
        </div>
        {profile.sourceCitations?.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end max-w-[55%]">
            {profile.sourceCitations.slice(0, 4).map((url, i) => {
              let host = url;
              try {
                host = new URL(url).hostname.replace(/^www\./, "");
              } catch {
                /* ignore */
              }
              return (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-0.5 rounded-full text-xs border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 truncate max-w-[160px]"
                  title={url}
                >
                  {host}
                </a>
              );
            })}
            {profile.sourceCitations.length > 4 && (
              <Chip>+{profile.sourceCitations.length - 4}</Chip>
            )}
          </div>
        )}
      </div>

      {/* Narrative */}
      {profile.companyNarrative && (
        <p className="text-sm text-muted-foreground leading-relaxed">
          {profile.companyNarrative}
        </p>
      )}

      {/* Meta row */}
      <div className="flex flex-wrap gap-2">
        {localeText && (
          <Chip>
            <span className="inline-flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {localeText}
            </span>
          </Chip>
        )}
        <Chip>
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="w-3 h-3" />
            Weekend: {weekendOff}
          </span>
        </Chip>
      </div>

      {/* Branches */}
      {profile.branches?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
            Branches
          </p>
          <div className="flex flex-wrap gap-2">
            {profile.branches.map((b, i) => (
              <div
                key={i}
                className={cn(
                  "rounded-md border px-3 py-2 bg-white min-w-[140px]",
                  b.isHq ? "border-primary/40" : "border-border",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium truncate">{b.name}</span>
                  {b.isHq && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary font-medium">
                      HQ
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {b.city}
                  {b.country ? `, ${b.country}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Departments */}
      {profile.departments?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
            Departments
          </p>
          <ul className="space-y-1">
            {profile.departments.map((d, i) => {
              const pct = Math.round((d.headcountRatio ?? 0) * 100);
              const sampleTitles = (d.titles ?? []).slice(0, 2).join(", ");
              return (
                <li
                  key={i}
                  className="flex items-center justify-between gap-3 text-sm rounded-md px-3 py-1.5 bg-white border border-border"
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{d.name}</span>
                    {sampleTitles && (
                      <span className="text-xs text-muted-foreground ml-2 truncate">
                        · {sampleTitles}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-mono text-muted-foreground tabular-nums">
                    {pct}%
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Feature flags */}
      {profile.featureFlagsToEnable?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
            Modules Enabled
          </p>
          <div className="flex flex-wrap gap-1.5">
            {profile.featureFlagsToEnable.map((flag) => (
              <span
                key={flag}
                className="px-2 py-0.5 rounded-full text-xs border border-green-200 bg-green-50 text-green-700"
              >
                {flag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Seeding;
